import React, { useRef } from 'react';
import { connect } from '../../redux/store';
import { compose, withHandlers, withState, lifecycle } from 'recompose';
import { Link } from '@reach/router';
import {
  ErrorMessage,
  LoadingMessage,
  C,
  H,
  ReportSiteIssueWrapper,
} from '@kineticdata/bundle-common';
import { Modal, ModalBody } from 'reactstrap';
import { ActionMetaWrapper } from './ActionMetaWrapper';
import { CommentsList } from '../shared/CommentsList';
import { IssuesList } from '../shared/IssuesList';
import { tabLabels } from '../../redux/modules/actions';
import { actions } from '../../redux/modules/submission';
import { getStatus, getActionStatusColor, isActiveClass } from '../../utils';
import { ContentManagerReview } from '../shared/content_manager/ContentManagerReview';
import { PageTitle } from '../shared/PageTitle';
import { CoreForm, I18n } from '@kineticdata/react';
import { getIn } from 'immutable';
import { parse } from 'query-string';

const Layout = connect(state => ({
  reportForm: H.selectReportSiteIssueForm(state),
}))(({ form, content, lockMessage, reviewPaginationControl, reportForm }) => (
  <ReportSiteIssueWrapper form={form} reportForm={reportForm}>
    {lockMessage}
    {content}
    {reviewPaginationControl}
  </ReportSiteIssueWrapper>
));

const ActionComponent = ({ submission, error, pending, ...props }) => {
  const actionFormRef = useRef(null);
  const scrollActionFormIntoView = () => {
    if (actionFormRef.current)
      actionFormRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="page-container">
      <div className="page-panel">
        <ActionMetaWrapper submission={submission} navigate={props.navigate}>
          {meta => (
            <>
              <PageTitle
                className="page-title-heading--sm"
                parts={[submission && submission.label, 'Actions']}
                breadcrumbs={[
                  {
                    label: (
                      <span>
                        <span className="fa fa-fw fa-md fa-arrow-left text-muted" />
                        <I18n>{props.typeLabel}</I18n>
                      </span>
                    ),
                    to: props.mode ? '../../..' : '../..',
                  },
                ]}
                title={
                  !error &&
                  submission &&
                  (submission.values[C.FIELD_ACTION_SUMMARY] ||
                    submission.label)
                }
                suptitle={
                  !error &&
                  submission && (
                    <>
                      <strong>
                        <I18n>
                          {submission.values[C.FIELD_REQUEST_FORM_NAME] ||
                            submission.form.name}
                        </I18n>
                      </strong>
                      {!!submission.values[C.FIELD_REQUEST_SUBMISSION_ID] && (
                        <>
                          {' \u00b7 '}
                          {submission.values[C.FIELD_REQUEST_SUBMISSION_ID]
                            .slice(-6)
                            .toUpperCase()}
                        </>
                      )}
                    </>
                  )
                }
                subtitle={
                  !error &&
                  submission &&
                  submission.values[C.FIELD_ACTION_DETAILS]
                }
                meta={
                  !error &&
                  submission &&
                  [
                    {
                      value: (
                        <span
                          className={`badge badge-${getActionStatusColor(
                            submission,
                          )} badge-stylized text-muted`}
                        >
                          <I18n>{getStatus(submission)}</I18n>
                        </span>
                      ),
                    },
                    {
                      label: 'Created',
                      value: meta.showCreatedDate(),
                    },
                    {
                      label: 'Submitter',
                      value: meta.showSubmitter(),
                    },
                    { label: 'Completed', value: meta.showCompletedDate() },
                    { value: meta.showIssues() },
                  ].filter(m => !!m.value)
                }
                actions={
                  !error &&
                  submission &&
                  [
                    {
                      label: 'Add Comment',
                      onClick: meta.handleAddComment(),
                      menu: true,
                    },
                    {
                      label: 'Add Issue',
                      onClick: meta.handleAddIssue(),
                      menu: true,
                    },
                    submission.parent && {
                      label: 'View Primary Request',
                      to: `${props.appLocation}/submissions/${
                        submission.parent.id
                      }`,
                      menu: true,
                    },
                  ].filter(a => !!a && (!!a.onClick || !!a.to))
                }
              />

              {error && (
                <ErrorMessage
                  title="Failed to load request"
                  message={error.message}
                />
              )}
              {!error && !submission && <LoadingMessage />}
              {!error &&
                submission && (
                  <>
                    <div className="bg-light p-4 mb-5 rounded">
                      <div className="submission-tabs p-0">
                        <ul className="nav nav-tabs" role="tablist">
                          <li role="tab" className="nav-item">
                            <Link
                              to={props.mode ? '..' : '.'}
                              getProps={isActiveClass('nav-link')}
                            >
                              <I18n>Review and Resolve</I18n>
                            </Link>
                          </li>

                          {meta.hasRequestParent && (
                            <li role="tab" className="nav-item">
                              <Link
                                to={props.mode ? '../issues' : 'issues'}
                                getProps={isActiveClass(
                                  `nav-link ${
                                    props.hasUnresolvedIssues
                                      ? 'text-danger'
                                      : ''
                                  }`,
                                )}
                              >
                                <I18n>Issues</I18n>
                                {props.hasUnresolvedIssues && (
                                  <span className="large-asterisk text-danger">
                                    {' '}
                                    *
                                  </span>
                                )}
                                {props.issuesCount ? (
                                  <span
                                    className={`badge badge-pill badge-${
                                      props.hasUnresolvedIssues
                                        ? 'danger'
                                        : 'subtle'
                                    }`}
                                  >
                                    {' '}
                                    {props.issuesCount}
                                  </span>
                                ) : null}
                              </Link>
                            </li>
                          )}

                          {meta.hasRequestParent && (
                            <li role="tab" className="nav-item">
                              <Link
                                to={props.mode ? '../comments' : 'comments'}
                                getProps={isActiveClass('nav-link')}
                              >
                                <I18n>Comments</I18n>
                                {props.commentsCount ? (
                                  <span className="badge badge-pill badge-subtle">
                                    {' '}
                                    {props.commentsCount}
                                  </span>
                                ) : null}
                              </Link>
                            </li>
                          )}
                        </ul>

                        <div className="submission-tabs__content">
                          {props.mode === 'issues' && meta.hasRequestParent ? (
                            <IssuesList
                              issues={props.issues}
                              handleAddIssue={meta.handleAddIssue()}
                            />
                          ) : props.mode === 'comments' &&
                          meta.hasRequestParent ? (
                            <CommentsList
                              submission={submission}
                              handleAddComment={meta.handleAddComment()}
                            />
                          ) : (
                            <>
                              {submission.values[
                                C.FIELD_REQUEST_SUBMISSION_ID
                              ] &&
                                !meta.hideRequestReview && (
                                  <I18n
                                    submissionId={
                                      submission.values[
                                        C.FIELD_REQUEST_SUBMISSION_ID
                                      ]
                                    }
                                  >
                                    <div className="text-center mt-n3 mb-3">
                                      <button
                                        className="btn btn-link"
                                        onClick={scrollActionFormIntoView}
                                      >
                                        <span className="fa fa-fw fa-arrow-down" />
                                        <I18n>Scroll to Action</I18n>
                                        <span className="fa fa-fw fa-arrow-down" />
                                      </button>
                                    </div>
                                    {meta.usesContentManager ? (
                                      <ContentManagerReview
                                        id={
                                          submission.values[
                                            C.FIELD_REQUEST_SUBMISSION_ID
                                          ]
                                        }
                                      />
                                    ) : (
                                      <CoreForm
                                        submission={
                                          submission.values[
                                            C.FIELD_REQUEST_SUBMISSION_ID
                                          ]
                                        }
                                        review
                                      />
                                    )}
                                    <hr />
                                  </I18n>
                                )}

                              <div ref={actionFormRef}>
                                {props.hasUnresolvedIssues && (
                                  <div className="alert alert-bar alert-danger">
                                    <div>
                                      <I18n>
                                        The primary request for this action has
                                        pending issues. Please review the issues
                                        prior to addressing this action.
                                      </I18n>
                                    </div>
                                    <div className="text-right">
                                      <Link
                                        to={props.mode ? '../issues' : 'issues'}
                                        className="btn btn-outline-danger btn-animate"
                                      >
                                        <I18n>View Issues</I18n>
                                        <span className="fa fa-fw fa-arrow-right" />
                                      </Link>
                                    </div>
                                  </div>
                                )}
                                <I18n
                                  context={`kapps.${props.kapp.slug}.forms.${
                                    submission.form.slug
                                  }`}
                                >
                                  <CoreForm
                                    submission={submission.id}
                                    review={
                                      submission.coreState !==
                                      C.CORE_STATE_DRAFT
                                    }
                                    lock={true}
                                    completed={props.actionPageCompleted}
                                    layoutComponent={Layout}
                                  />
                                </I18n>
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
            </>
          )}
        </ActionMetaWrapper>
      </div>
    </div>
  );
};

// Helper functions for selecting the decision in the action modal
const markCompleted = form =>
  form.getFieldByName('Action Status').value('Completed');
const markCancelled = form =>
  form.getFieldByName('Action Status').value('Cancelled by User');

const ActionModalComponent = ({ submission, error, ...props }) => {
  return (
    <Modal isOpen={true} toggle={props.toggle} size="lg">
      <div className="modal-header">
        <h4 className="modal-title">
          <button type="button" className="btn btn-link" onClick={props.toggle}>
            <I18n>Close</I18n>
          </button>
          <span>
            <I18n>Action</I18n>
          </span>
        </h4>
      </div>
      <ModalBody>
        <ActionMetaWrapper submission={submission} navigate={props.navigate}>
          {meta => (
            <div className="p-3">
              <PageTitle
                className="page-title-heading--xs pt-0"
                title={
                  !error &&
                  submission &&
                  (submission.values[C.FIELD_ACTION_SUMMARY] ||
                    submission.label)
                }
                suptitle={
                  !error &&
                  submission && (
                    <>
                      <strong>
                        <I18n>
                          {submission.values[C.FIELD_REQUEST_FORM_NAME] ||
                            submission.form.name}
                        </I18n>
                      </strong>
                      {!!submission.values[C.FIELD_REQUEST_SUBMISSION_ID] && (
                        <>
                          {' \u00b7 '}
                          {submission.values[C.FIELD_REQUEST_SUBMISSION_ID]
                            .slice(-6)
                            .toUpperCase()}
                        </>
                      )}
                    </>
                  )
                }
                subtitle={
                  !error &&
                  submission &&
                  submission.values[C.FIELD_ACTION_DETAILS]
                }
                meta={
                  !error &&
                  submission &&
                  [
                    {
                      value: (
                        <span
                          className={`badge badge-${getActionStatusColor(
                            submission,
                          )} badge-stylized text-muted`}
                        >
                          <I18n>{getStatus(submission)}</I18n>
                        </span>
                      ),
                    },
                    {
                      label: 'Created',
                      value: meta.showCreatedDate(),
                    },
                    {
                      label: 'Submitter',
                      value: meta.showSubmitter(),
                    },
                    { label: 'Completed', value: meta.showCompletedDate() },
                    { value: meta.showIssues() },
                  ].filter(m => !!m.value)
                }
              />

              {error && (
                <ErrorMessage
                  title="Failed to load request"
                  message={error.message}
                />
              )}
              {!error && !submission && <LoadingMessage />}
              {!error &&
                submission && (
                  <div>
                    {props.hasUnresolvedIssues && (
                      <div className="alert alert-bar alert-danger">
                        <div>
                          <I18n>
                            The primary request for this action has pending
                            issues. Please review the issues prior to addressing
                            this action.
                          </I18n>
                        </div>
                        <div className="text-right">
                          <Link
                            to={
                              props.mode
                                ? '../issues'
                                : `action/${submission.id}/issues`
                            }
                            className="btn btn-outline-danger btn-animate"
                          >
                            <I18n>View Issues</I18n>
                            <span className="fa fa-fw fa-arrow-right" />
                          </Link>
                        </div>
                      </div>
                    )}
                    <I18n
                      context={`kapps.${props.kapp.slug}.forms.${
                        submission.form.slug
                      }`}
                    >
                      <CoreForm
                        submission={submission.id}
                        review={submission.coreState !== C.CORE_STATE_DRAFT}
                        lock={true}
                        loaded={
                          props.isCompleted
                            ? markCompleted
                            : props.isCancelled
                              ? markCancelled
                              : undefined
                        }
                        completed={props.completed}
                        layoutComponent={Layout}
                      />
                    </I18n>
                  </div>
                )}
            </div>
          )}
        </ActionMetaWrapper>
      </ModalBody>
    </Modal>
  );
};

const issueStatusSortOrder = [
  'Pending',
  'Being Addressed',
  'Resolved',
  'Cancelled',
];
const sortIssuesByCreatedAt = (a, b) =>
  a.createdAt === b.createdAt ? 0 : a.createdAt < b.createdAt ? -1 : 1;
const sortIssuesByStatus = (a, b) =>
  a.values['Status'] === b.values['Status']
    ? sortIssuesByCreatedAt(a, b)
    : issueStatusSortOrder.indexOf(a.values['Status']) -
      issueStatusSortOrder.indexOf(b.values['Status']);

export const mapStateToProps = (state, props) => {
  const submission =
    state.submission.data && state.submission.data.id === props.submissionId
      ? state.submission.data
      : null;
  const issues =
    submission && submission.parent && submission.parent.descendants
      ? submission.parent.descendants
          .filter(d => d.type === 'Issue')
          .sort(sortIssuesByStatus)
      : null;
  const issuesCount = issues ? issues.length : null;
  const commentsCount = getIn(
    submission || {},
    submission && submission.type === C.REQUEST_FORM_TYPE
      ? ['activities']
      : ['parent', 'activities'],
    [],
  ).filter(a => a.type === 'Comment').length;

  return {
    kapp: state.app.kapp,
    profile: state.app.profile,
    submission,
    error: state.submission.error,
    pending: state.submission.pending,
    typeLabel: `${(tabLabels.find(t => t.type === props.type) || {}).label ||
      ''} Actions`.trim(),
    mode: props.mode,
    appLocation: state.app.location,
    location: state.router.location,
    isSmallLayout: state.app.layoutSize === 'small',
    issues,
    issuesCount,
    hasUnresolvedIssues:
      !!issues &&
      issues.filter(i => i.coreState === C.CORE_STATE_DRAFT).length > 0,
    commentsCount,
  };
};

export const mapDispatchToProps = {
  fetchSubmission: actions.fetchSubmissionRequest,
  clearSubmission: actions.clearSubmissionRequest,
  startPoller: actions.startSubmissionPoller,
  stopPoller: actions.stopSubmissionPoller,
};

const enhance = compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  withState('returnToRequest', 'setReturnToRequest', false),
  withHandlers({
    actionPageCompleted: props => () => {
      props.navigate(
        props.returnToRequest && props.submission && props.submission.parent
          ? `${props.appLocation}/requests/${
              typeof props.returnToRequest === 'string'
                ? `${props.returnToRequest}/`
                : ''
            }request/${props.submission.parent.id}/activity`
          : props.mode
            ? '../../..'
            : '../..',
      );
    },
  }),
  lifecycle({
    componentDidMount() {
      this.props.fetchSubmission(this.props.submissionId);
      this.props.startPoller(this.props.submissionId);
      // If returnToRequest param is provided, set state so we can redirect back
      // to the request when the action is completed
      const params = parse(this.props.location.search);
      this.props.setReturnToRequest(
        params.returnToRequest || params.returnToRequest === '',
      );
    },
    componentDidUpdate(prevProps) {
      if (this.props.submissionId !== prevProps.submissionId) {
        this.props.stopPoller();
        this.props.fetchSubmission(this.props.submissionId);
        this.props.startPoller(this.props.submissionId);
      }
    },
    componentWillUnmount() {
      this.props.clearSubmission();
      this.props.stopPoller();
    },
  }),
);

export const Action = enhance(ActionComponent);
export const ActionModal = enhance(ActionModalComponent);
